// The control for a Multi-select custom field on every surface that edits one: a design-system dropdown field
// showing the chosen names (CustomFieldMultiSelectText) that opens a checkbox list of the field's options under
// a Select all row. Ticking changes the selection the field shows but saves nothing; the selection is saved
// once, when the list closes, and only if it differs from the stored one. An empty selection clears the value.
// Select all is ticked when every option is, a dash when some are; ticking it ticks every option the field has
// now, unticking it clears them all, so an option added later is never ticked on its own. No search, no limit.
// When names are hidden ("+N", or the first name cut), hovering over or focusing the field shows every name in
// the design-system hover box. Disabled, the field cannot open but still shows the names and the hover box. A
// click on the field or in its list never reaches the row behind it.
import React, {useEffect, useRef, useState} from 'react';
import Styled from 'styled-components';
import {useIntl} from 'react-intl';
import {ButtonDropdownField, DropdownList, Separator, Tooltip, tokensForThemeProvider} from '@forecast-it/design-system';
import {CustomFieldMultiSelectText} from './CustomFieldMultiSelectText';
import {MULTI_SELECT_SEPARATOR} from './multiSelectFit';
import {useMultiSelectSelection} from './useMultiSelectSelection';
import {CustomFieldTooltipText} from '../forecast-app/shared/components/custom-field-columns/CustomFieldTooltipText';
import {dropdownOptionKey, multiSelectNames} from '../forecast-app/shared/util/customFieldValueValidation';

const tokens = tokensForThemeProvider.sky.sys;

// The field fills its cell and may shrink to it. The design-system list opens at z-index 100 and takes no other;
// in the task modal the comments footer below the fields sits at 100 too and, later in the page, covers the list.
// One higher keeps an open list on top of it. Only a list that is not portalled sits inside the field.
const Frame = Styled.div`
	width: 100%;
	min-width: 0;
	& [data-radix-popper-content-wrapper] {
		z-index: 101 !important;
	}
`;
// The hover box's trigger, around the field rather than on it: a disabled field takes no pointer events, and the
// tree stays the same whether the field is disabled or not.
const HoverArea = Styled.div`
	width: 100%;
`;
// Room around the design-system separator between Select all and the options it acts on.
const SelectAllSeparator = Styled.div`
	margin: ${tokens.spacing['200']} 0;
`;

type Option = {id: string; displayName: string};

export type CustomFieldMultiSelectInputProps = {
	// The stored value: a JSON array of option ids.
	value: string | null | undefined;
	// The field's options with their GraphQL ids, in the option list's order.
	options: ReadonlyArray<Option>;
	// The field's name, which starts the field's accessible name.
	label: string;
	// Called once as the list closes, when the selection changed: the JSON array of the ticked ids in the option
	// list's order, or null when none is ticked.
	onCommit: (value: string | null) => void;
	// The user cannot edit the record, or another save on it is in flight.
	disabled?: boolean;
	// Opens the list in a portal, for a table row that clips what overflows it. Never inside a modal, which a
	// portalled list would open behind.
	portalled?: boolean;
	'data-cy'?: string;
};

export const CustomFieldMultiSelectInput = ({
	value,
	options,
	label,
	onCommit,
	disabled = false,
	portalled = false,
	'data-cy': dataCy = 'custom-field-multi-select-input',
}: CustomFieldMultiSelectInputProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const keys = options.map(option => dropdownOptionKey(option.id));
	const selection = useMultiSelectSelection(value, keys);
	const {chosen, chosenValue, selectAll, toggle, toggleAll} = selection;
	// Whether the list is open, so a close reported twice saves once.
	const open = useRef(false);
	// What was ticked as the list opened: closing saves only when the ticks differ from it.
	const openedWith = useRef<string[]>([]);
	// A stored value that changes while the list is open (another save of the record, a refetch) must not undo
	// the ticks being made; it is shown once the list closes without a change.
	useEffect(() => {
		if (!open.current) selection.reset(value);
	}, [value]);
	const [hidden, setHidden] = useState(false);

	const names = multiSelectNames(chosenValue, options).join(MULTI_SELECT_SEPARATOR);
	const showTooltip = hidden && chosen.length > 0;

	// The latest render's stored value and callback, for a close that comes as the field unmounts, after its last
	// render; the hook reads the latest ticks the same way.
	const latest = useRef({value, onCommit});
	latest.current = {value, onCommit};
	// Ends an open list: saves the ticks if they changed, or else shows the stored value as it is now.
	const close = (): boolean => {
		if (!open.current) return false;
		open.current = false;
		if (selection.changedFrom(openedWith.current)) {
			latest.current.onCommit(selection.latestValue());
			return true;
		}
		return false;
	};
	// A list still open as the field goes away (a row scrolled out, the panel closed) saves like any close.
	useEffect(() => () => void close(), []);

	const [isOpen, setIsOpen] = useState(false);
	const onOpenChange = (next: boolean) => {
		setIsOpen(next);
		if (next) {
			open.current = true;
			openedWith.current = chosen;
			return;
		}
		if (!close()) selection.reset(latest.current.value);
	};
	// The list closes on Escape from a document listener that lets the key go on to the window, where the task
	// modal closes on Escape too. Taken first, on the window's capture phase, Escape closes only the list, as it
	// does a Dropdown's, and saves like any other close.
	useEffect(() => {
		if (!isOpen) return undefined;
		const closeOnEscape = (event: KeyboardEvent): void => {
			if (event.key !== 'Escape') return;
			event.stopPropagation();
			onOpenChange(false);
		};
		window.addEventListener('keydown', closeOnEscape, true);
		return () => window.removeEventListener('keydown', closeOnEscape, true);
	}, [isOpen]);

	const ariaLabel =
		chosen.length === 0
			? label
			: formatMessage({id: 'custom_field.multi_select.aria_label'}, {field: label, values: names});
	// The hover box opens on its trigger's focus and pointer move unless an earlier handler on the trigger
	// cancelled the event. Cancelling while every name is visible keeps it shut without adding or removing the
	// Tooltip around the trigger, which would remount the trigger and lose its focus.
	const keepTooltipShut = (event: React.SyntheticEvent): void => {
		if (!showTooltip) event.preventDefault();
	};
	const tooltipContent = <CustomFieldTooltipText>{names}</CustomFieldTooltipText>;
	const field = (
		<ButtonDropdownField
			size="small"
			disabled={disabled}
			isDefaultLabel={chosen.length === 0}
			aria-label={ariaLabel}
			data-cy={dataCy}
		>
			{chosen.length === 0 ? (
				formatMessage({id: 'common.select_value'})
			) : (
				<CustomFieldMultiSelectText
					value={chosenValue}
					options={options}
					label={label}
					tooltip={false}
					onHiddenChange={setHidden}
					data-cy={`${dataCy}-text`}
				/>
			)}
		</ButtonDropdownField>
	);

	// One tree whether the field is disabled or not: a surface that disables the field while a save is in flight
	// (the ticket panel) must not remount it, which would lose its focus. Disabled, the trigger cannot open the
	// list, and the area around it takes the focus and pointer the disabled button cannot, to open the hover box.
	// The hover box listens on that area in both states; a focus or pointer move on the field reaches it too.
	return (
		<Frame onClick={e => e.stopPropagation()}>
			<DropdownList open={isOpen} onOpenChange={onOpenChange}>
				{/* Interactable only so the design-system Tooltip does not cancel the click that opens the list. */}
				<Tooltip content={tooltipContent} interactable>
					<HoverArea
						role={disabled && showTooltip ? 'group' : undefined}
						aria-label={disabled && showTooltip ? ariaLabel : undefined}
						tabIndex={disabled && showTooltip ? 0 : undefined}
						onFocus={keepTooltipShut}
						onPointerMove={keepTooltipShut}
						data-cy={`${dataCy}-hover-area`}
					>
						<DropdownList.Trigger disabled={disabled}>{field}</DropdownList.Trigger>
					</HoverArea>
				</Tooltip>
				<DropdownList.Content offset={4} portalled={portalled}>
					{keys.length > 0 ? (
						<>
							<DropdownList.CheckboxItem
								checked={selectAll}
								onChange={toggleAll}
								data-cy={`${dataCy}-select-all`}
							>
								{formatMessage({id: 'common.select_all'})}
							</DropdownList.CheckboxItem>
							<SelectAllSeparator>
								<Separator />
							</SelectAllSeparator>
						</>
					) : null}
					{options.map((option, index) => (
						<DropdownList.CheckboxItem
							key={option.id}
							checked={chosen.includes(keys[index])}
							onChange={() => toggle(keys[index])}
							data-cy={`${dataCy}-option-${keys[index]}`}
						>
							{option.displayName}
						</DropdownList.CheckboxItem>
					))}
				</DropdownList.Content>
			</DropdownList>
		</Frame>
	);
};

export default CustomFieldMultiSelectInput;
